import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./SurveyListFilters.scss";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TablePagination from "@mui/material/TablePagination";
import Checkbox from "@mui/material/Checkbox";
import Chip from "@mui/material/Chip";
import Buttons from "../../../components/Buttons/Buttons";
import SearchInput from "../../../components/SearchInput/SearchInput";
import SelectInput from "../../../components/SelectInput/SelectInput";
import TableStatusRow from "../../../components/TableStatusRow/TableStatusRow";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import { BackArrow } from "../../../assets/icons-components/Icons";
import { onKeyActivate } from "../../../utils/a11y";
import {
  SURVEY_ASSIGNMENT_STATUS_FILTER_OPTIONS,
  ASSIGNMENT_STATUS_CHIP_CONFIG,
} from "../../../utils/surveysConstants";
import useSurveyResults from "./useSurveyResults";

const RESULTS_TABLE_COLUMN_COUNT = 6;

export default function SurveyResultsListPage() {
  const navigate = useNavigate();
  const {
    surveyTitle,
    searchQuery,
    statusFilter,
    rows,
    page,
    rowsPerPage,
    totalRows,
    selectedIds,
    isLoading,
    fetchError,
    hasActiveFilters,
    handleSearchChange,
    handleStatusFilterChange,
    handleClearFilters,
    handleChangePage,
    handleChangeRowsPerPage,
    retryFetch,
    handleView,
    handleDownloadOne,
    handleDownloadSelected,
    revokeDialog,
    handleRevokeClick,
    handleRevokeSelectedClick,
    confirmRevoke,
    toggleSelectRow,
    toggleSelectAll,
  } = useSurveyResults();

  const handleBack = () => navigate("/surveys");
  const selectedIdSet = useMemo(() => new Set(selectedIds), [selectedIds]);

  return (
    <div className="main1">

      <div className="main1_header" style={{ padding: "12px 0", width: "100%" }}>
        <div className="main1_header_left">
          <div
            className="main1_header_left_arrow"
            onClick={handleBack}
            onKeyDown={onKeyActivate(handleBack)}
            role="button"
            tabIndex={0}
          >
            <BackArrow onClick={handleBack} />
            <h3>{surveyTitle ? `${surveyTitle} — Results` : "Survey Results"}</h3>
          </div>
        </div>
      </div>

      <div className="survey-filters-bar">
        <div className="survey-filters-bar_fields">
          <div className="survey-filters-bar_fields_search">
            <SearchInput
              name="searchQuery"
              placeholder="Search by patient name"
              value={searchQuery}
              onChange={handleSearchChange}
            />
          </div>
          <div className="survey-filters-bar_fields_status">
            <SelectInput
              name="statusFilter"
              value={statusFilter}
              onChange={handleStatusFilterChange}
              options={SURVEY_ASSIGNMENT_STATUS_FILTER_OPTIONS}
              placeholder="All Statuses"
              clearable
            />
          </div>
        </div>
        <div className="survey-filters-bar_actions">
          {hasActiveFilters && (
            <Buttons
              text="Clear Filters"
              type="button"
              variant="smallsecondary"
              style={{ height: 40 }}
              onClick={handleClearFilters}
            />
          )}
          <Buttons
            text={`Download Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
            type="button"
            variant="primary"
            fullWidth={false}
            style={{ height: 40 }}
            onClick={handleDownloadSelected}
            disabled={selectedIds.length === 0}
          />
          <Buttons
            text={`Revoke Selected${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
            type="button"
            variant="smallsecondary"
            fullWidth={false}
            style={{ height: 40 }}
            onClick={handleRevokeSelectedClick}
            disabled={selectedIds.length === 0}
          />
        </div>
      </div>

      <div className="table-container">
        <TableContainer>
          <Table sx={{ minWidth: 650 }}>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    size="small"
                    checked={rows.length > 0 && selectedIds.length === rows.length}
                    indeterminate={selectedIds.length > 0 && selectedIds.length < rows.length}
                    onChange={toggleSelectAll}
                    disabled={rows.length === 0}
                  />
                </TableCell>
                <TableCell>Patient</TableCell>
                <TableCell>Assigned</TableCell>
                <TableCell>Submitted</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.length === 0 ? (
                <TableStatusRow
                  colSpan={RESULTS_TABLE_COLUMN_COUNT}
                  loading={isLoading}
                  error={fetchError}
                  onRetry={retryFetch}
                  emptyMessage="No survey assignments found"
                />
              ) : (
                rows.map((row) => {
                  const chip = ASSIGNMENT_STATUS_CHIP_CONFIG[(row.statusName || "").toLowerCase()] || {
                    label: row.statusName,
                    color: "default",
                  };
                  return (
                  <TableRow key={row.id}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selectedIdSet.has(row.id)}
                        onChange={() => toggleSelectRow(row.id)}
                      />
                    </TableCell>
                    <TableCell>{row.patientName}</TableCell>
                    <TableCell>{row.assignedAt ? new Date(row.assignedAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>{row.submittedAt ? new Date(row.submittedAt).toLocaleString() : "—"}</TableCell>
                    <TableCell>
                      <Chip label={chip.label} color={chip.color} size="small" />
                    </TableCell>
                    <TableCell>
                      <Buttons
                        text="View"
                        type="button"
                        variant="smallsecondary"
                        onClick={() => handleView(row)}
                        disabled={!row.submittedAt}
                        style={{ marginRight: 8 }}
                      />
                      <Buttons
                        text="Download"
                        type="button"
                        variant="smallsecondary"
                        onClick={() => handleDownloadOne(row)}
                        disabled={!row.submittedAt}
                        style={{ marginRight: 8 }}
                      />
                      <Buttons
                        text="Revoke"
                        type="button"
                        variant="smallsecondary"
                        onClick={() => handleRevokeClick(row)}
                        disabled={(row.statusName || "").toLowerCase() === "revoked"}
                      />
                    </TableCell>
                  </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <TablePagination
          rowsPerPageOptions={[10, 25, 50]}
          component="div"
          count={totalRows}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </div>

      {revokeDialog.open && (
        <ConfirmDialog
          open={revokeDialog.open}
          onClose={revokeDialog.closeDialog}
          title="Revoke Survey Access"
          severity="warning"
          content={
            revokeDialog.target?.label ? (
              <span>
                Are you sure you want to revoke survey access for <strong>{revokeDialog.target.label}</strong>? They will no longer be able to complete this survey.
              </span>
            ) : (
              <span>
                Are you sure you want to revoke survey access for <strong>{revokeDialog.target?.ids?.length || 0} selected patient(s)</strong>? They will no longer be able to complete this survey.
              </span>
            )
          }
          buttonText="Yes, Revoke"
          additionalButtonText="Cancel"
          onButtonClick={confirmRevoke}
          onAdditionalButtonClick={revokeDialog.closeDialog}
        />
      )}
    </div>
  );
}
